import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  MapPin, Users, Calendar, Send, Loader2, ArrowRight, Plus, X, Check,
  Sparkles, Compass, Car, UtensilsCrossed, Heart, Phone, Mail, MessageCircle,
  ChevronLeft, ChevronRight, ChevronDown, Instagram, Globe, ShieldCheck, Trash2, Copy,
  HelpCircle,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useBooking } from "@/contexts/BookingContext";
import { HOTELS, EXPERIENCES } from "@/lib/data";
import { isBivouac, getAccommodationTypes } from "@/lib/propertyType";
import { BOARD_ORDER, BOARD_FIELD_LABEL, pickBoardLabel } from "@/lib/boardBasis";
import { SERVICE_CATALOGUE as SHARED_SERVICE_CATALOGUE } from "@/lib/serviceCatalogue";
import ExtrasGrid from "@/components/booking/ExtrasGrid";
import BrandMonogramEdge from "@/components/BrandMonogramEdge";
import PageHero from "@/components/PageHero";
import PropertyThumb from "@/components/PropertyThumb";
import SyncedPropertyCarousel from "@/components/SyncedPropertyCarousel";
import PropertyCardMiniMap from "@/components/PropertyCardMiniMap";
import HowToBookSection from "@/components/HowToBookSection";
import BookingInfoModal from "@/components/booking/BookingInfoModal";
import { devLog } from "../lib/devLog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ───────────────────────────────────────────────────────────────────────────
// Helpers
const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

const useIsMobile = (breakpoint = 768) => {
  const [is, setIs] = useState(
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onResize = () => setIs(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return is;
};

// ───────────────────────────────────────────────────────────────────────────
// Static copy
const COPY = {
  hero_overline: { en: "Booking · Enquiry", fr: "Réservation · Demande", es: "Reserva · Solicitud" },
  hero_title_a: { en: "Plan a journey", fr: "Imaginer un voyage", es: "Planear un viaje" },
  hero_title_b: { en: "through the South.", fr: "à travers le sud.", es: "a través del sur." },
  hero_sub: {
    en: "Not a booking engine. A conversation with a host. Tell us the houses, the dates and the textures of the trip you're dreaming — we'll write the rest, by hand, within 24 hours.",
    fr: "Pas un moteur de réservation. Une conversation avec un hôte. Indiquez-nous les maisons, les dates et les textures du voyage que vous rêvez — nous écrirons le reste, à la main, sous 24 heures.",
    es: "No es un motor de reservas. Una conversación con un anfitrión. Indícanos las casas, las fechas y las texturas del viaje que sueñas — escribiremos el resto, a mano, en menos de 24 horas.",
  },
  hero_cta: { en: "Begin the enquiry", fr: "Commencer la demande", es: "Comenzar la solicitud" },
  hero_secondary: { en: "How it works", fr: "Comment ça marche", es: "Cómo funciona" },

  // Hero channel picker — mirrors the three-door pattern used on the
  // /contact hero. On /booking the "detailed" channel is the current
  // page, so it renders as the active card. The other two link out to
  // the classic booking flow and the general inquiry tab.
  channel_detailed_label: { en: "Detailed Booking", fr: "Demande détaillée", es: "Reserva detallada" },
  channel_quick_label: { en: "Quick Booking", fr: "Demande rapide", es: "Reserva rápida" },
  channel_inquiry_label: { en: "General Inquiry", fr: "Demande générale", es: "Consulta general" },
  channel_current: { en: "Selected · you're here", fr: "Sélectionné · vous y êtes", es: "Seleccionado · estás aquí" },
  channel_open: { en: "Open this form", fr: "Ouvrir ce formulaire", es: "Abrir formulario" },

  hotels_overline: { en: "Step 01 · Houses", fr: "Étape 01 · Maisons", es: "Paso 01 · Casas" },
  hotels_title: { en: "Choose your house", fr: "Choisir votre maison", es: "Elige tu casa" },
  hotels_sub: {
    en: "Nine addresses between Marrakech and the Sahara. Add as many as you like to a single enquiry — many guests stitch a kasbah, a bivouac and a refuge together.",
    fr: "Neuf adresses entre Marrakech et le Sahara. Ajoutez-en autant que vous voulez à une seule demande — beaucoup d'hôtes tissent une kasbah, un bivouac et un refuge.",
    es: "Nueve direcciones entre Marrakech y el Sáhara. Añade cuantas quieras a una sola solicitud — muchos huéspedes combinan kasbah, vivac y refugio.",
  },
  request_stay: { en: "Request stay", fr: "Demander un séjour", es: "Solicitar estancia" },
  in_enquiry: { en: "In your enquiry", fr: "Dans votre demande", es: "En tu solicitud" },

  services_overline: { en: "Step 02 · Experiences", fr: "Étape 02 · Expériences", es: "Paso 02 · Experiencias" },
  services_title: { en: "Add experiences", fr: "Ajouter des expériences", es: "Añadir experiencias" },
  services_sub: {
    en: "Bundle excursions, spa rituals, transfers or a curated itinerary into the same enquiry. Multi-select — your host will price and propose.",
    fr: "Regroupez excursions, rituels de spa, transferts ou un itinéraire sur mesure dans la même demande. Sélection multiple — votre hôte chiffre et propose.",
    es: "Agrupa excursiones, rituales de spa, traslados o un itinerario a medida en la misma solicitud. Selección múltiple — tu anfitrión cotiza y propone.",
  },

  form_overline: { en: "Step 03 · Your details", fr: "Étape 03 · Vos coordonnées", es: "Paso 03 · Tus datos" },
  form_title: { en: "Tell us about the trip", fr: "Parlez-nous du voyage", es: "Háblenos del viaje" },
  form_sub: {
    en: "The more texture you give us, the more bespoke the proposal that comes back.",
    fr: "Plus vous nous donnez de matière, plus la proposition qui revient sera sur mesure.",
    es: "Cuanta más textura nos des, más a medida será la propuesta que recibas.",
  },
  submit: { en: "Send enquiry", fr: "Envoyer la demande", es: "Enviar solicitud" },
  sending: { en: "Sending…", fr: "Envoi…", es: "Enviando…" },
  success: { en: "Thank you — a host will be in touch within 24 hours.", fr: "Merci — un hôte vous répond sous 24 heures.", es: "Gracias — un anfitrión le responderá en 24 horas." },
  error: { en: "We couldn't send the enquiry. Please try again.", fr: "Nous n'avons pas pu envoyer la demande. Réessayez.", es: "No pudimos enviar la solicitud. Inténtelo de nuevo." },
  empty_items: { en: "Please add at least one stay or experience to your enquiry.", fr: "Ajoutez au moins un séjour ou une expérience à votre demande.", es: "Añade al menos una estancia o experiencia a tu solicitud." },

  // Inline-validation soft toast — fired alongside the highlighted
  // fields + scroll-into-view so the user has both peripheral and
  // foveal feedback.
  validation_summary: {
    en: "Please complete the highlighted fields below.",
    fr: "Veuillez compléter les champs en surbrillance ci-dessous.",
    es: "Por favor, completa los campos resaltados a continuación.",
  },
  // Per-field error messages — kept short, direct and in lower case
  // style consistent with the rest of the form micro-copy.
  err_full_name: {
    en: "Please enter your full name",
    fr: "Veuillez saisir votre nom complet",
    es: "Por favor, introduce tu nombre completo",
  },
  err_email_missing: {
    en: "Please enter your email address",
    fr: "Veuillez saisir votre adresse e-mail",
    es: "Por favor, introduce tu correo electrónico",
  },
  err_email_invalid: {
    en: "Please enter a valid email address",
    fr: "Veuillez saisir une adresse e-mail valide",
    es: "Por favor, introduce un correo electrónico válido",
  },

  // Toasts emitted when a HotelCard CTA is clicked. The {name} token is
  // replaced at runtime with the property's display name so the user
  // gets a precise confirmation of what just happened. Copy is kept
  // location-agnostic ("to your enquiry") because the user is *not*
  // scrolled anywhere — the card grid and the booking widget at the top
  // are two synchronized views onto the same enquiry state, so the
  // confirmation simply acknowledges the addition.
  added_to_planner: {
    en: "{name} added to your enquiry",
    fr: "{name} ajouté à votre demande",
    es: "{name} añadido a tu solicitud",
  },
  removed_from_planner: {
    en: "{name} removed from your enquiry",
    fr: "{name} retiré de votre demande",
    es: "{name} eliminado de tu solicitud",
  },

  info_overline: { en: "How it works", fr: "Comment ça marche", es: "Cómo funciona" },
  info_title: { en: "Every booking starts with", fr: "Chaque réservation commence par", es: "Cada reserva empieza con" },
  info_title_accent: { en: "a conversation.", fr: "une conversation.", es: "una conversación." },

  ota_overline: { en: "Or via", fr: "Ou via", es: "O a través de" },
  ota_title: { en: "Booking platforms", fr: "Plateformes de réservation", es: "Plataformas de reserva" },
  ota_sub: {
    en: "Most properties are also bookable through your preferred platform. Direct enquiries unlock the full Xaluca experience — itinerary, host, in-house extras.",
    fr: "La plupart des maisons sont aussi réservables via votre plateforme habituelle. La demande directe ouvre l'expérience Xaluca complète — itinéraire, hôte, extras maison.",
    es: "La mayoría de las casas también son reservables a través de tu plataforma preferida. La consulta directa abre la experiencia Xaluca completa — itinerario, anfitrión, extras de la casa.",
  },

  contact_overline: { en: "Get in touch", fr: "Nous joindre", es: "Contacto" },
  contact_title: { en: "A host, a face,", fr: "Un hôte, un visage,", es: "Un anfitrión, un rostro," },
  contact_title_accent: { en: "a real reply.", fr: "une vraie réponse.", es: "una respuesta real." },
};

// Editorial service catalogue — categories the user can multi-select.
// Imported from `@/lib/serviceCatalogue` so the homepage widget +
// StickySummary + this page all read the same source-of-truth list.
const SERVICE_CATALOGUE = SHARED_SERVICE_CATALOGUE;

const INFO_STEPS = [
  {
    title: { en: "Send your enquiry", fr: "Envoyez votre demande", es: "Envía tu solicitud" },
    desc: { en: "Houses, dates, services, the people in your party — all in one form.", fr: "Maisons, dates, services, voyageurs — tout en un formulaire.", es: "Casas, fechas, servicios, viajeros — todo en un formulario." },
  },
  {
    title: { en: "A host replies in 24h", fr: "Un hôte répond en 24h", es: "Un anfitrión responde en 24h" },
    desc: { en: "Availability, a draft programme, recommendations — written by a real person, in your language.", fr: "Disponibilités, programme proposé, recommandations — écrits par un humain, dans votre langue.", es: "Disponibilidad, programa propuesto, recomendaciones — escritos por un humano, en tu idioma." },
  },
  {
    title: { en: "Refine together", fr: "Affinez ensemble", es: "Afinen juntos" },
    desc: { en: "Adjust dates, swap a hotel, add a chef's dinner — by email, phone or WhatsApp.", fr: "Ajustez les dates, changez un hôtel, ajoutez un dîner du chef — par e-mail, téléphone ou WhatsApp.", es: "Ajusta fechas, cambia un hotel, añade una cena del chef — por correo, teléfono o WhatsApp." },
  },
  {
    title: { en: "Confirm & arrive", fr: "Confirmez & arrivez", es: "Confirma y llega" },
    desc: { en: "Final programme, transfer plan, packing notes — and the door wide open.", fr: "Programme final, plan des transferts, conseils de bagage — et la porte grande ouverte.", es: "Programa final, plan de traslados, consejos de equipaje — y la puerta abierta." },
  },
];

const OTA_PLATFORMS = [
  {
    name: "Booking.com",
    href: "https://www.booking.com/searchresults.html?ss=Xaluca+Morocco",
    tagline: { en: "Find availability across the collection.", fr: "Trouvez les disponibilités de la collection.", es: "Encuentre disponibilidad en la colección." },
    accent: "#003580",
  },
  {
    name: "Expedia",
    href: "https://www.expedia.com/Hotel-Search?destination=Xaluca%20Morocco",
    tagline: { en: "Bundle flights, transfers and stays.", fr: "Combinez vols, transferts et séjours.", es: "Combine vuelos, traslados y estancias." },
    accent: "#FFC72C",
  },
];

// ───────────────────────────────────────────────────────────────────────────
// Sub-components

const Hero = ({ lang, onSecondaryClick, onOpenQuick, onOpenInquiry }) => {
  const channels = [
    { id: "detailed", icon: Sparkles, label: COPY.channel_detailed_label, active: true, onClick: null },
    { id: "quick", icon: Compass, label: COPY.channel_quick_label, active: false, onClick: onOpenQuick },
    { id: "inquiry", icon: HelpCircle, label: COPY.channel_inquiry_label, active: false, onClick: onOpenInquiry },
  ];
  return (
    <section data-testid="booking-hero">
      <PageHero
        images={[
          "https://customer-assets.emergentagent.com/job_xaluca-luxury/artifacts/qrtoq576_Kasbah%20Hotel%20Xaluca%20-%20Ae%CC%81reas%20-%20HamidBabni-5.jpg",
          "https://customer-assets.emergentagent.com/job_xaluca-luxury/artifacts/htd6p1fu_Kasbah%20Hotel%20Xaluca%20-%20Ae%CC%81reas%20-%20HamidBabni-4.jpg",
          "https://customer-assets.emergentagent.com/job_xaluca-luxury/artifacts/6pvzp897_Kasbah%20Hotel%20Xaluca%20-%20Ae%CC%81reas%20-%20HamidBabni-1.jpg",
        ]}
        alt="Booking · Grup Xaluca"
        scope="page.booking"
        overline={pick(COPY.hero_overline, lang)}
        title={pick(COPY.hero_title_a, lang)}
        titleAccent={pick(COPY.hero_title_b, lang)}
        sub={pick(COPY.hero_sub, lang)}
        onSecondaryClick={onSecondaryClick}
        secondaryLabel={pick(COPY.hero_secondary, lang)}
        extraCtas={
          <div
            className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4"
            data-testid="booking-hero-cta-overlay"
          >
            {channels.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={`booking-hero-cta-${tab.id}`}
                  type="button"
                  onClick={tab.active ? undefined : tab.onClick}
                  aria-pressed={tab.active}
                  aria-current={tab.active ? "page" : undefined}
                  disabled={tab.active}
                  className={`group text-left p-4 md:p-5 backdrop-blur-md transition-all duration-300 border-2 flex items-start gap-3 md:gap-4 ${
                    tab.active
                      ? "bg-[#C16542]/95 text-[#FDFBF7] border-[#D4A373] shadow-[0_12px_40px_-12px_rgba(193,101,66,0.65)] -translate-y-1 cursor-default"
                      : "bg-[#1A1513]/55 text-[#FDFBF7] border-[#FDFBF7]/25 hover:bg-[#1A1513]/75 hover:border-[#D4A373] hover:-translate-y-0.5"
                  }`}
                  data-testid={`booking-hero-cta-${tab.id}`}
                >
                  <span
                    className={`shrink-0 inline-flex items-center justify-center w-9 h-9 md:w-10 md:h-10 rounded-full transition-colors ${
                      tab.active ? "bg-[#FDFBF7]/15" : "bg-[#FDFBF7]/10 group-hover:bg-[#D4A373]/20"
                    }`}
                  >
                    <Icon className={`w-4 h-4 md:w-5 md:h-5 ${tab.active ? "text-[#FDFBF7]" : "text-[#D4A373]"}`} strokeWidth={1.6} />
                  </span>
                  <span className="flex flex-col min-w-0 flex-1">
                    <span className="font-serif-x text-base md:text-lg leading-tight">
                      {pick(tab.label, lang)}
                    </span>
                    <span className={`text-[10px] md:text-[11px] tracking-[0.18em] uppercase mt-1.5 ${tab.active ? "text-[#FDFBF7]/85" : "text-[#FDFBF7]/65"}`}>
                      {pick(tab.active ? COPY.channel_current : COPY.channel_open, lang)}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        }
      />
    </section>
  );
};

const HotelDetailPanel = ({ slug, detail, onChange, lang }) => {
  const bivouac = isBivouac(slug);
  const accomTypes = useMemo(() => getAccommodationTypes(slug), [slug]);
  const labels = {
    check_in: { en: "Check-in", fr: "Arrivée", es: "Llegada" },
    check_out: { en: "Check-out", fr: "Départ", es: "Salida" },
    adults: { en: "Adults", fr: "Adultes", es: "Adultos" },
    children: { en: "Children", fr: "Enfants", es: "Niños" },
    infants: { en: "Infants", fr: "Bébés", es: "Bebés" },
    room_type: bivouac
      ? { en: "Tent type", fr: "Type de tente", es: "Tipo de tienda" }
      : { en: "Room type", fr: "Type de chambre", es: "Tipo de habitación" },
    notes: { en: "Notes for this property", fr: "Notes pour cette maison", es: "Notas para esta casa" },
    any: { en: "Any (we'll suggest)", fr: "Indifférent (nous proposerons)", es: "Indistinto (sugeriremos)" },
    add_room: bivouac
      ? { en: "Add another tent", fr: "Ajouter une tente", es: "Añadir tienda" }
      : { en: "Add another room", fr: "Ajouter une chambre", es: "Añadir habitación" },
    room_n: bivouac
      ? { en: "Tent", fr: "Tente", es: "Tienda" }
      : { en: "Room", fr: "Chambre", es: "Habitación" },
    copy_below: bivouac
      ? { en: "Copy to tents below", fr: "Copier vers les tentes ci-dessous", es: "Copiar a las tiendas siguientes" }
      : { en: "Copy to rooms below", fr: "Copier vers les chambres ci-dessous", es: "Copiar a las habitaciones siguientes" },
    bulk_or: { en: "or", fr: "ou", es: "o" },
    bulk_label: bivouac
      ? { en: "Bulk add tents", fr: "Ajout rapide", es: "Añadir varias" }
      : { en: "Bulk add rooms", fr: "Ajout rapide", es: "Añadir varias" },
    bulk_generate: bivouac
      ? { en: "Generate tents", fr: "Générer les tentes", es: "Generar tiendas" }
      : { en: "Generate rooms", fr: "Générer les chambres", es: "Generar habitaciones" },
  };
  const [bulkCount, setBulkCount] = useState("");
  const rooms = Array.isArray(detail.rooms) ? detail.rooms : [];
  const setRoom = (idx, key, value) => {
    const next = rooms.map((r, i) => (i === idx ? { ...r, [key]: value } : r));
    onChange("rooms", next);
  };
  const addRoom = () => {
    onChange("rooms", [...rooms, { room_type: "", adults: 2, children: 0, infants: 0, board_basis: "breakfast" }]);
  };
  const removeRoom = (idx) => {
    if (rooms.length <= 1) return;
    onChange("rooms", rooms.filter((_, i) => i !== idx));
  };
  const generateRooms = () => {
    const parsed = parseInt(bulkCount, 10);
    if (!Number.isFinite(parsed)) return;
    const n = Math.max(1, Math.min(50, parsed));
    const current = rooms.length;
    if (n === current) return;
    let next;
    if (n > current) {
      const additions = [];
      for (let i = current; i < n; i++) additions.push({ room_type: "", adults: 2, children: 0, infants: 0, board_basis: "breakfast" });
      next = [...rooms, ...additions];
    } else {
      next = rooms.slice(0, n);
    }
    onChange("rooms", next);
    setBulkCount("");
  };
  const copyRoomBelow = (idx) => {
    const src = rooms[idx];
    if (!src) return;
    const fields = ["room_type", "adults", "children", "infants", "board_basis"];
    const next = rooms.map((r, i) => {
      if (i <= idx) return r;
      const copied = { ...r };
      fields.forEach((k) => { copied[k] = src[k]; });
      return copied;
    });
    onChange("rooms", next);
  };
  const inputCls = "w-full bg-white border border-[#2C2621]/15 hover:border-[#C16542] focus:border-[#C16542] outline-none px-3 py-2 text-sm";
  const hotel = HOTELS.find((h) => h.slug === slug) || null;
  const propertyTypeLabel = bivouac
    ? { fr: "Bivouac · Désert", en: "Bivouac · Desert", es: "Vivac · Desierto" }[lang] || "Bivouac"
    : (hotel?.category === "refuge"
      ? { fr: "Refuge · Montagne", en: "Refuge · Mountain", es: "Refugio · Montaña" }[lang] || "Refuge"
      : { fr: "Hôtel · Kasbah", en: "Hotel · Kasbah", es: "Hotel · Kasbah" }[lang] || "Hotel");
  return (
    <div className="px-6 md:px-7 pb-6 pt-1 bg-[#F2EBE1]/40 border-t border-[#D4A373]/30" data-testid={`booking-hotel-detail-${slug}`}>
      <div className="flex items-center gap-4 mb-4">
        <PropertyThumb
          slug={slug}
          alt={hotel?.name || slug}
          aspectRatio="4/3"
          className="w-28 h-20 md:w-36 md:h-28 shrink-0"
          testId={`booking-hotel-${slug}-thumb`}
        />
        <div className="min-w-0">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#A07042]">
            {propertyTypeLabel}
          </p>
          <p className="font-serif-x text-base md:text-lg text-[#2C2621] leading-tight truncate mt-0.5">
            {hotel?.name || slug}
          </p>
          {hotel?.location ? (
            <p className="text-[11px] text-[#5C5248] mt-0.5 truncate">{pick(hotel.location, lang)}</p>
          ) : null}
        </div>
      </div>
      <p className="text-[10px] tracking-[0.3em] uppercase text-[#A07042] mb-3">
        {pick({ en: "Details for this stay", fr: "Détails de ce séjour", es: "Detalles de esta estancia" }, lang)}
      </p>
      {/* Property-level dates */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <label className="flex flex-col">
          <span className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248] mb-1">{pick(labels.check_in, lang)}</span>
          <input type="date" value={detail.check_in} onChange={(e) => onChange("check_in", e.target.value)} className={inputCls} data-testid={`booking-hotel-${slug}-checkin`} />
        </label>
        <label className="flex flex-col">
          <span className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248] mb-1">{pick(labels.check_out, lang)}</span>
          <input type="date" value={detail.check_out} onChange={(e) => onChange("check_out", e.target.value)} className={inputCls} data-testid={`booking-hotel-${slug}-checkout`} />
        </label>
      </div>

      {/* Rooms as independently editable sub-cards */}
      <div className="space-y-3">
        {rooms.map((room, idx) => (
          <div
            key={`room-${idx}`}
            className="bg-white border border-[#2C2621]/12 p-3.5"
            data-testid={`booking-hotel-${slug}-room-${idx}`}
          >
            <div className="flex items-center justify-between gap-3 mb-3">
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#A07042]">
                {pick(labels.room_n, lang)} {String(idx + 1).padStart(2, "0")}
              </p>
              <div className="flex items-center gap-2">
                {idx < rooms.length - 1 ? (
                  <button
                    type="button"
                    onClick={() => copyRoomBelow(idx)}
                    className="inline-flex items-center gap-1 px-2 py-1 border border-[#2C2621]/15 hover:border-[#C16542] text-[#5C5248] hover:text-[#C16542] text-[10px] tracking-[0.18em] uppercase transition-colors"
                    aria-label={pick(labels.copy_below, lang)}
                    title={pick(labels.copy_below, lang)}
                    data-testid={`booking-hotel-${slug}-room-${idx}-copy-below`}
                  >
                    <Copy className="w-3 h-3" strokeWidth={1.7} />
                    <span className="hidden sm:inline">{pick(labels.copy_below, lang)}</span>
                  </button>
                ) : null}
                {rooms.length > 1 ? (
                  <button
                    type="button"
                    onClick={() => removeRoom(idx)}
                    className="inline-flex items-center gap-1 px-2 py-1 border border-[#2C2621]/15 hover:border-[#A35133] text-[#5C5248] hover:text-[#A35133] text-[10px] tracking-[0.18em] uppercase transition-colors"
                    data-testid={`booking-hotel-${slug}-room-${idx}-remove`}
                  >
                    <Trash2 className="w-3 h-3" strokeWidth={1.7} />
                  </button>
                ) : null}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="flex flex-col col-span-2">
                <span className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248] mb-1">{pick(labels.room_type, lang)}</span>
                <select
                  value={room.room_type || ""}
                  onChange={(e) => setRoom(idx, "room_type", e.target.value)}
                  className={inputCls + " appearance-none bg-no-repeat"}
                  style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path fill='%235C5248' d='M5 6 0 0h10z'/></svg>\")", backgroundPosition: "right 0.75rem center", paddingRight: "1.75rem" }}
                  data-testid={`booking-hotel-${slug}-room-${idx}-roomtype`}
                >
                  <option value="">{pick(labels.any, lang)}</option>
                  {accomTypes.map((opt) => (
                    <option key={opt.key} value={pick(opt.name, lang)}>
                      {pick(opt.name, lang)}
                      {opt.capacity ? ` · ${pick(opt.capacity, lang)}` : ""}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col">
                <span className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248] mb-1">{pick(labels.adults, lang)}</span>
                <input type="number" min="1" value={room.adults} onChange={(e) => setRoom(idx, "adults", Number(e.target.value) || 1)} className={inputCls} data-testid={`booking-hotel-${slug}-room-${idx}-adults`} />
              </label>
              <label className="flex flex-col">
                <span className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248] mb-1">{pick(labels.children, lang)}</span>
                <input type="number" min="0" value={room.children} onChange={(e) => setRoom(idx, "children", Number(e.target.value) || 0)} className={inputCls} data-testid={`booking-hotel-${slug}-room-${idx}-children`} />
              </label>
              <label className="flex flex-col">
                <span className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248] mb-1">{pick(labels.infants, lang)}</span>
                <input type="number" min="0" value={room.infants || 0} onChange={(e) => setRoom(idx, "infants", Number(e.target.value) || 0)} className={inputCls} data-testid={`booking-hotel-${slug}-room-${idx}-infants`} />
              </label>
              <div className="flex flex-col col-span-2">
                <span className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248] mb-1">{pick(BOARD_FIELD_LABEL, lang)}</span>
                <div className="flex flex-wrap gap-1.5">
                  {BOARD_ORDER.map((key) => {
                    const active = room.board_basis === key;
                    return (
                      <button
                        type="button"
                        key={key}
                        onClick={() => setRoom(idx, "board_basis", key)}
                        aria-pressed={active}
                        className={`text-[11px] tracking-[0.18em] uppercase px-2.5 py-2 border transition-colors ${
                          active
                            ? "bg-[#C16542] border-[#C16542] text-[#FDFBF7]"
                            : "bg-[#FDFBF7] border-[#2C2621]/15 text-[#5C5248] hover:border-[#C16542] hover:text-[#C16542]"
                        }`}
                        data-testid={`booking-hotel-${slug}-room-${idx}-board-${key}`}
                      >
                        {pickBoardLabel(key, lang, { bivouac })}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={addRoom}
          className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#2C2621]/15 hover:border-[#C16542] text-[#5C5248] hover:text-[#C16542] text-[10px] tracking-[0.22em] uppercase transition-colors"
          data-testid={`booking-hotel-${slug}-add-room`}
        >
          <Plus className="w-3 h-3" strokeWidth={1.7} />
          {pick(labels.add_room, lang)}
        </button>
        <span className="text-[10px] tracking-[0.22em] uppercase text-[#5C5248]/55">
          {pick(labels.bulk_or, lang)}
        </span>
        <div
          className="inline-flex items-center gap-1.5 px-2 py-1.5 border border-[#2C2621]/15 bg-white"
          data-testid={`booking-hotel-${slug}-bulk-group`}
        >
          <input
            type="number"
            min="1"
            max="50"
            value={bulkCount}
            onChange={(e) => setBulkCount(e.target.value.replace(/[^0-9]/g, ""))}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                generateRooms();
              }
            }}
            placeholder="5"
            className="w-14 bg-white border border-[#2C2621]/15 hover:border-[#C16542] focus:border-[#C16542] outline-none px-2 py-1 text-sm text-[#2C2621] tabular-nums text-center"
            aria-label={pick(labels.bulk_label, lang)}
            data-testid={`booking-hotel-${slug}-bulk-count`}
          />
          <button
            type="button"
            onClick={generateRooms}
            disabled={!bulkCount}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] text-[10px] tracking-[0.22em] uppercase transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid={`booking-hotel-${slug}-bulk-generate`}
          >
            {pick(labels.bulk_generate, lang)}
          </button>
        </div>
      </div>

      {/* Property-level notes */}
      <label className="flex flex-col mt-4">
        <span className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248] mb-1">{pick(labels.notes, lang)}</span>
        <textarea rows={2} value={detail.notes} onChange={(e) => onChange("notes", e.target.value)} placeholder={pick({ en: "Connecting rooms, anniversary, early arrival…", fr: "Chambres communicantes, anniversaire, arrivée tôt…", es: "Habitaciones comunicadas, aniversario, llegada temprana…" }, lang)} className={inputCls + " resize-none"} data-testid={`booking-hotel-${slug}-notes`} />
      </label>
    </div>
  );
};

const HotelCard = ({ hotel, lang, selected, onToggle, detail, onChange }) => (
  <article
    className={`group relative bg-[#FDFBF7] border transition-all duration-500 ${
      selected ? "border-[#C16542] shadow-[0_20px_60px_-30px_rgba(193,101,66,0.55)]" : "border-[#2C2621]/10 hover:border-[#D4A373]/60"
    }`}
    data-testid={`booking-hotel-card-${hotel.slug}`}
    data-cms-scope={`booking-hotel-card.${hotel.slug}`}
  >
    <div className="relative overflow-hidden aspect-[4/3]">
      {/* 8-image carousel shared with /contact via lib/propertyGalleries.js.
          Reads CMS overrides from /admin/galleries → "Hotel card images". */}
      <SyncedPropertyCarousel
        slug={hotel.slug}
        alt={hotel.name}
        testIdPrefix={`booking-hotel-card-${hotel.slug}-thumb`}
        aspectRatio="4/3"
        className="absolute inset-0"
        overlays={
          <>
            <div className="absolute inset-0 bg-gradient-to-t from-[#1A1513]/85 via-[#1A1513]/10 to-transparent" />
            {/* Xaluca X monogram — flush to the bottom-right of the image */}
            <BrandMonogramEdge
              size="md"
              opacity={0.45}
              testIdPrefix={`booking-hotel-card-${hotel.slug}`}
            />
            <span className="absolute top-4 left-4 inline-block text-[10px] tracking-[0.3em] uppercase text-[#FDFBF7] bg-[#1A1513]/55 backdrop-blur-sm px-2.5 py-1">
              {hotel.category === "bivouac" ? pick({ en: "Bivouac", fr: "Bivouac", es: "Vivac" }, lang)
                : hotel.category === "refuge" ? pick({ en: "Refuge", fr: "Refuge", es: "Refugio" }, lang)
                : pick({ en: "Hotel", fr: "Hôtel", es: "Hotel" }, lang)}
            </span>
            {selected && (
              <span className="absolute top-4 right-4 inline-flex items-center justify-center w-8 h-8 bg-[#C16542] text-[#FDFBF7] rounded-full">
                <Check className="w-4 h-4" strokeWidth={2} />
              </span>
            )}
          </>
        }
      />
    </div>
    <div className="p-6 md:p-7 flex flex-col gap-3">
      <div className="flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#A07042]">
        <MapPin className="w-3 h-3" strokeWidth={1.5} />
        {hotel.location}
      </div>
      <h3 className="font-serif-x text-2xl md:text-[26px] leading-tight tracking-tight text-[#2C2621]">
        {hotel.name}
      </h3>
      <p className="text-sm text-[#5C5248] leading-relaxed line-clamp-3">
        {pick(hotel.intro, lang)}
      </p>
      {hotel.amenities && (
        <div className="flex flex-wrap gap-1.5 mt-1">
          {hotel.amenities.slice(0, 3).map((a) => (
            <span key={`am-${hotel.slug}-${a}`} className="text-[10px] tracking-[0.2em] uppercase text-[#5C5248]/80 border border-[#2C2621]/15 px-2 py-1">
              {a}
            </span>
          ))}
        </div>
      )}
      {/* Compact satellite mini-map — mirrors the "About the hotel" map on
          the property page. Non-interactive to keep 14 side-by-side maps
          performant on mobile. */}
      <PropertyCardMiniMap
        hotelSlug={hotel.slug}
        hotelName={hotel.name}
        hotelLocation={hotel.location}
        hotelCategory={hotel.category}
        aspectRatio="16/9"
        className="mt-2"
      />
      <button
        type="button"
        onClick={() => onToggle(hotel.slug)}
        className={`mt-4 inline-flex items-center justify-center gap-2 px-5 py-3 text-[10px] tracking-[0.3em] uppercase transition-colors ${
          selected
            ? "bg-[#2C2621] text-[#FDFBF7] hover:bg-[#1A1513]"
            : "bg-[#C16542] text-[#FDFBF7] hover:bg-[#A35133]"
        }`}
        data-testid={`booking-hotel-toggle-${hotel.slug}`}
      >
        {selected ? (
          <>
            <Check className="w-3.5 h-3.5" strokeWidth={1.8} />
            {pick(COPY.in_enquiry, lang)}
          </>
        ) : (
          <>
            <Plus className="w-3.5 h-3.5" strokeWidth={1.8} />
            {pick(COPY.request_stay, lang)}
          </>
        )}
      </button>
    </div>
    {selected && detail && (
      <HotelDetailPanel slug={hotel.slug} detail={detail} onChange={(k, v) => onChange(hotel.slug, k, v)} lang={lang} />
    )}
  </article>
);

const HotelsStep = ({ hotels, selectedHotels, toggleHotel, updateHotelDetail, lang }) => (
  <div data-testid="booking-step-hotels">
    <div className="grid md:grid-cols-12 gap-8 mb-12">
      <div className="md:col-span-7">
        <p className="overline">{pick(COPY.hotels_overline, lang)}</p>
        <h2 className="font-serif-x mt-4 text-4xl md:text-5xl leading-[1.05] tracking-tight">
          {pick(COPY.hotels_title, lang)}
        </h2>
      </div>
      <div className="md:col-span-5 flex md:items-end">
        <p className="text-base md:text-lg leading-relaxed text-[#5C5248]">{pick(COPY.hotels_sub, lang)}</p>
      </div>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
      {hotels.map((h) => (
        <HotelCard
          key={`bh-${h.slug}`}
          hotel={h}
          lang={lang}
          selected={Boolean(selectedHotels[h.slug])}
          detail={selectedHotels[h.slug]}
          onToggle={toggleHotel}
          onChange={updateHotelDetail}
        />
      ))}
    </div>
  </div>
);

const ServicesStep = ({ selectedServices, toggleService, updateServiceDetail, lang }) => (
  <div data-testid="booking-step-services">
    <div className="grid md:grid-cols-12 gap-8 mb-12">
      <div className="md:col-span-7">
        <p className="overline">{pick(COPY.services_overline, lang)}</p>
        <h2 className="font-serif-x mt-4 text-4xl md:text-5xl leading-[1.05] tracking-tight">
          {pick(COPY.services_title, lang)}
        </h2>
      </div>
      <div className="md:col-span-5 flex md:items-end">
        <p className="text-base md:text-lg leading-relaxed text-[#5C5248]">{pick(COPY.services_sub, lang)}</p>
      </div>
    </div>
    <ExtrasGrid
      lang={lang}
      selectedServices={selectedServices}
      toggleService={toggleService}
      updateServiceDetail={updateServiceDetail}
      testIdPrefix="booking"
    />
  </div>
);

const Field = ({ label, children, span = "md:col-span-6", error, errorId }) => (
  <label className={`flex flex-col ${span}`}>
    <span className="text-[10px] tracking-[0.3em] uppercase text-[#5C5248] mb-2">{label}</span>
    {children}
    {error ? (
      <span
        id={errorId}
        role="alert"
        className="mt-1.5 inline-flex items-center gap-1.5 text-[12px] text-[#B7301B] leading-snug"
        data-testid={errorId}
      >
        <span aria-hidden="true" className="inline-block w-1 h-1 rounded-full bg-[#B7301B]" />
        {error}
      </span>
    ) : null}
  </label>
);

const inputCls = "w-full border border-[#2C2621]/15 bg-[#FDFBF7] px-4 py-3 text-[15px] text-[#2C2621] placeholder:text-[#5C5248]/45 focus:outline-none focus:border-[#C16542] transition-colors";
// Same surface but with the editorial terracotta-red error state
// (border + faint blush wash so the field reads as "incomplete" without
// breaking the warm palette of the page).
const inputErrCls = "w-full border border-[#B7301B] bg-[#FBEFEA] px-4 py-3 text-[15px] text-[#2C2621] placeholder:text-[#5C5248]/45 focus:outline-none focus:border-[#B7301B] transition-colors";

const DetailsStep = ({ form, setForm, lang, errors, clearError, nameInputRef, emailInputRef, phoneInputRef }) => {
  const labels = {
    full_name: { en: "Full name", fr: "Nom complet", es: "Nombre completo" },
    email: { en: "Email", fr: "E-mail", es: "Correo electrónico" },
    phone: { en: "Phone", fr: "Téléphone", es: "Teléfono" },
    whatsapp: { en: "WhatsApp (optional)", fr: "WhatsApp (optionnel)", es: "WhatsApp (opcional)" },
    check_in: { en: "Check-in", fr: "Arrivée", es: "Llegada" },
    check_out: { en: "Check-out", fr: "Départ", es: "Salida" },
    adults: { en: "Adults", fr: "Adultes", es: "Adultos" },
    children: { en: "Children", fr: "Enfants", es: "Niños" },
    rooms: { en: "Rooms", fr: "Chambres", es: "Habitaciones" },
    room_preference: { en: "Room preference", fr: "Préférence de chambre", es: "Preferencia de habitación" },
    transport_needs: { en: "Transport needs", fr: "Transports souhaités", es: "Necesidades de transporte" },
    special_occasion: { en: "Special occasion", fr: "Occasion spéciale", es: "Ocasión especial" },
    dietary: { en: "Dietary requirements", fr: "Régime alimentaire", es: "Requisitos alimentarios" },
    message: { en: "Notes & dreams", fr: "Notes & rêves", es: "Notas y sueños" },
  };
  const set = (k, v) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    // Auto-clear the inline error the moment the user starts typing in
    // a previously-flagged required field. Empty/whitespace-only input
    // is still considered incomplete — re-validation runs on submit.
    if (errors && errors[k] && typeof v === "string" && v.trim().length > 0) {
      clearError(k);
    }
  };

  return (
    <div data-testid="booking-step-details">
      <div className="grid md:grid-cols-12 gap-8 mb-10">
        <div className="md:col-span-7">
          <p className="overline">{pick(COPY.form_overline, lang)}</p>
          <h2 className="font-serif-x mt-4 text-4xl md:text-5xl leading-[1.05] tracking-tight">
            {pick(COPY.form_title, lang)}
          </h2>
        </div>
        <div className="md:col-span-5 flex md:items-end">
          <p className="text-base md:text-lg leading-relaxed text-[#5C5248]">{pick(COPY.form_sub, lang)}</p>
        </div>
      </div>

      <div className="grid md:grid-cols-12 gap-5 md:gap-6">
        <Field
          label={pick(labels.full_name, lang)}
          span="md:col-span-6"
          error={errors?.full_name}
          errorId="booking-input-name-error"
        >
          <input
            ref={nameInputRef}
            id="booking-input-name"
            data-testid="booking-input-name"
            required
            aria-required="true"
            aria-invalid={errors?.full_name ? "true" : "false"}
            aria-describedby={errors?.full_name ? "booking-input-name-error" : undefined}
            value={form.full_name}
            onChange={(e) => set("full_name", e.target.value)}
            className={errors?.full_name ? inputErrCls : inputCls}
          />
        </Field>
        <Field
          label={pick(labels.email, lang)}
          span="md:col-span-6"
          error={errors?.email}
          errorId="booking-input-email-error"
        >
          <input
            ref={emailInputRef}
            id="booking-input-email"
            type="email"
            data-testid="booking-input-email"
            required
            aria-required="true"
            aria-invalid={errors?.email ? "true" : "false"}
            aria-describedby={errors?.email ? "booking-input-email-error" : undefined}
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            className={errors?.email ? inputErrCls : inputCls}
          />
        </Field>
        <Field
          label={pick(labels.phone, lang)}
          span="md:col-span-6"
          error={errors?.phone}
          errorId="booking-input-phone-error"
        >
          <input
            ref={phoneInputRef}
            id="booking-input-phone"
            data-testid="booking-input-phone"
            aria-invalid={errors?.phone ? "true" : "false"}
            aria-describedby={errors?.phone ? "booking-input-phone-error" : undefined}
            value={form.phone}
            onChange={(e) => set("phone", e.target.value)}
            className={errors?.phone ? inputErrCls : inputCls}
          />
        </Field>
        <Field label={pick(labels.whatsapp, lang)} span="md:col-span-6">
          <input data-testid="booking-input-whatsapp" value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} className={inputCls} />
        </Field>

        <Field label={pick(labels.check_in, lang)} span="md:col-span-3">
          <input type="date" data-testid="booking-input-checkin" value={form.check_in} onChange={(e) => set("check_in", e.target.value)} className={inputCls} />
        </Field>
        <Field label={pick(labels.check_out, lang)} span="md:col-span-3">
          <input type="date" data-testid="booking-input-checkout" value={form.check_out} onChange={(e) => set("check_out", e.target.value)} className={inputCls} />
        </Field>
        <Field label={pick(labels.adults, lang)} span="md:col-span-2">
          <input type="number" min="1" data-testid="booking-input-adults" value={form.adults} onChange={(e) => set("adults", e.target.value)} className={inputCls} />
        </Field>
        <Field label={pick(labels.children, lang)} span="md:col-span-2">
          <input type="number" min="0" data-testid="booking-input-children" value={form.children} onChange={(e) => set("children", e.target.value)} className={inputCls} />
        </Field>
        <Field label={pick(labels.rooms, lang)} span="md:col-span-2">
          <input type="number" min="1" data-testid="booking-input-rooms" value={form.rooms} onChange={(e) => set("rooms", e.target.value)} className={inputCls} />
        </Field>

        <Field label={pick(labels.room_preference, lang)} span="md:col-span-6">
          <input data-testid="booking-input-room-pref" placeholder={pick({ en: "Suite, dune view, connecting rooms…", fr: "Suite, vue dunes, chambres communicantes…", es: "Suite, vista dunas, habitaciones comunicadas…" }, lang)} value={form.room_preference} onChange={(e) => set("room_preference", e.target.value)} className={inputCls} />
        </Field>
        <Field label={pick(labels.transport_needs, lang)} span="md:col-span-6">
          <input data-testid="booking-input-transport" placeholder={pick({ en: "Airport pickup, inter-hotel drive, 4×4 dune transfer…", fr: "Aéroport, transfert inter-hôtels, 4×4 dans les dunes…", es: "Aeropuerto, traslado entre hoteles, 4×4 en las dunas…" }, lang)} value={form.transport_needs} onChange={(e) => set("transport_needs", e.target.value)} className={inputCls} />
        </Field>

        <Field label={pick(labels.special_occasion, lang)} span="md:col-span-6">
          <input data-testid="booking-input-occasion" placeholder={pick({ en: "Honeymoon, birthday, anniversary…", fr: "Lune de miel, anniversaire…", es: "Luna de miel, cumpleaños, aniversario…" }, lang)} value={form.special_occasion} onChange={(e) => set("special_occasion", e.target.value)} className={inputCls} />
        </Field>
        <Field label={pick(labels.dietary, lang)} span="md:col-span-6">
          <input data-testid="booking-input-dietary" placeholder={pick({ en: "Vegetarian, halal, allergies…", fr: "Végétarien, halal, allergies…", es: "Vegetariano, halal, alergias…" }, lang)} value={form.dietary} onChange={(e) => set("dietary", e.target.value)} className={inputCls} />
        </Field>

        <Field label={pick(labels.message, lang)} span="md:col-span-12">
          <textarea
            data-testid="booking-input-message"
            rows={5}
            placeholder={pick({
              en: "Tell us about the trip you're imagining — slow days, dune evenings, the textures you'd like.",
              fr: "Parlez-nous du voyage que vous imaginez — journées lentes, soirées dans les dunes, les textures que vous aimeriez.",
              es: "Cuéntanos el viaje que imaginas — días lentos, atardeceres en las dunas, las texturas que deseas.",
            }, lang)}
            value={form.message}
            onChange={(e) => set("message", e.target.value)}
            className={inputCls + " resize-none"}
          />
        </Field>
      </div>
    </div>
  );
};

// ───────────────────────────────────────────────────────────────────────────
// Booking Information section
const BookingInformation = ({ lang }) => (
  <section className="py-24 md:py-32 bg-[#F2EBE1]/40 border-y border-[#2C2621]/10" data-testid="booking-information">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 mb-16">
        <div className="lg:col-span-5">
          <p className="overline">{pick(COPY.info_overline, lang)}</p>
          <h2 className="font-serif-x mt-5 text-4xl md:text-5xl lg:text-[56px] leading-[1.02] tracking-tight text-[#2C2621]">
            {pick(COPY.info_title, lang)}{" "}
            <em className="not-italic text-[#C16542]">{pick(COPY.info_title_accent, lang)}</em>
          </h2>
        </div>
        <div className="lg:col-span-7 lg:pt-2">
          <p className="text-base md:text-lg text-[#5C5248] leading-relaxed">
            {pick({
              en: "Every enquiry is handled by a host on the ground in Erfoud — not a call centre, not a chatbot. We answer in French, English, Spanish and Arabic, 7 days a week, with the same patience we'd give a friend planning their first trip south.",
              fr: "Chaque demande est traitée par un hôte basé à Erfoud — pas un centre d'appel, pas un chatbot. Nous répondons en français, anglais, espagnol et arabe, 7 jours sur 7, avec la même patience que pour un ami préparant son premier voyage dans le sud.",
              es: "Cada solicitud es atendida por un anfitrión sobre el terreno en Erfoud — no un call center, no un chatbot. Respondemos en francés, inglés, español y árabe, los 7 días, con la misma paciencia que daríamos a un amigo planeando su primer viaje al sur.",
            }, lang)}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#2C2621]/10 border border-[#2C2621]/10">
        {INFO_STEPS.map((s, i) => (
          <div key={`info-${s.title.en || i}`} className="bg-[#FDFBF7] p-7 md:p-8" data-testid={`booking-info-step-${i}`}>
            <span className="font-serif-x text-3xl text-[#C16542]">{String(i + 1).padStart(2, "0")}</span>
            <h3 className="mt-4 font-serif-x text-xl md:text-2xl tracking-tight leading-snug text-[#2C2621]">
              {pick(s.title, lang)}
            </h3>
            <p className="mt-3 text-sm text-[#5C5248] leading-relaxed">{pick(s.desc, lang)}</p>
          </div>
        ))}
      </div>

      <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-4 text-xs tracking-[0.25em] uppercase text-[#5C5248]/85">
        <span className="inline-flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#A07042]" strokeWidth={1.5} />{pick({ en: "No deposit to enquire", fr: "Aucun acompte requis", es: "Sin depósito para consultar" }, lang)}</span>
        <span className="inline-flex items-center gap-2"><Globe className="w-4 h-4 text-[#A07042]" strokeWidth={1.5} />{pick({ en: "FR · EN · ES · AR", fr: "FR · EN · ES · AR", es: "FR · EN · ES · AR" }, lang)}</span>
        <span className="inline-flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#A07042]" strokeWidth={1.5} />{pick({ en: "24h response", fr: "Réponse 24h", es: "Respuesta en 24h" }, lang)}</span>
      </div>
    </div>
  </section>
);

const OTAPlatforms = ({ lang }) => (
  <section className="py-24 md:py-32 bg-[#FDFBF7]" data-testid="booking-ota">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid md:grid-cols-12 gap-8 mb-12">
        <div className="md:col-span-6">
          <p className="overline">{pick(COPY.ota_overline, lang)}</p>
          <h2 className="font-serif-x mt-4 text-4xl md:text-5xl leading-[1.05] tracking-tight">
            {pick(COPY.ota_title, lang)}
          </h2>
        </div>
        <div className="md:col-span-6 flex md:items-end">
          <p className="text-base md:text-lg leading-relaxed text-[#5C5248]">{pick(COPY.ota_sub, lang)}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
        {OTA_PLATFORMS.map((p) => (
          <a
            key={`ota-${p.name}`}
            href={p.href}
            target="_blank"
            rel="noreferrer noopener"
            className="group relative overflow-hidden bg-[#FDFBF7] border border-[#2C2621]/10 hover:border-[#D4A373]/70 p-8 md:p-10 transition-colors"
            data-testid={`booking-ota-${p.name.toLowerCase().replace(/\./g, "")}`}
          >
            <span
              className="absolute inset-x-0 top-0 h-1"
              style={{ background: p.accent }}
              aria-hidden="true"
            />
            <div className="flex items-start justify-between gap-6">
              <div>
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#5C5248]/70">
                  {pick({ en: "Platform", fr: "Plateforme", es: "Plataforma" }, lang)}
                </p>
                <h3 className="mt-3 font-serif-x text-3xl md:text-4xl tracking-tight" style={{ color: p.accent }}>
                  {p.name}
                </h3>
                <p className="mt-4 text-sm md:text-base text-[#5C5248] leading-relaxed max-w-md">
                  {pick(p.tagline, lang)}
                </p>
              </div>
              <span className="inline-flex items-center justify-center w-11 h-11 border border-[#2C2621]/15 text-[#2C2621] group-hover:bg-[#2C2621] group-hover:text-[#FDFBF7] transition-colors">
                <ArrowRight className="w-4 h-4 -rotate-45" strokeWidth={1.5} />
              </span>
            </div>
          </a>
        ))}
      </div>
    </div>
  </section>
);

const GetInTouch = ({ lang }) => (
  <section id="get-in-touch" className="py-24 md:py-32 bg-[#14100E] text-[#FDFBF7] relative overflow-hidden" data-testid="booking-get-in-touch">
    <div className="absolute inset-0 pointer-events-none opacity-[0.07]" style={{ background: "radial-gradient(ellipse at bottom right, #D4A373 0%, transparent 55%)" }} />
    <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16">
      <div className="lg:col-span-5">
        <p className="overline !text-[#D4A373]">{pick(COPY.contact_overline, lang)}</p>
        <h2 className="font-serif-x mt-5 text-4xl md:text-5xl lg:text-[56px] leading-[1.02] tracking-tight">
          {pick(COPY.contact_title, lang)}
          <br />
          <em className="not-italic text-[#D4A373]">{pick(COPY.contact_title_accent, lang)}</em>
        </h2>
        <p className="mt-6 text-base md:text-lg text-[#FDFBF7]/80 leading-relaxed max-w-md">
          {pick({
            en: "Pick the channel that suits you. The same family answers every line.",
            fr: "Choisissez le canal qui vous convient. La même famille répond sur toutes les lignes.",
            es: "Elige el canal que prefieras. La misma familia responde en todas las líneas.",
          }, lang)}
        </p>
      </div>
      <div className="lg:col-span-7 grid sm:grid-cols-2 gap-px bg-[#FDFBF7]/10">
        <a href="mailto:xaluca@xaluca.com" className="bg-[#14100E] hover:bg-[#1A1513] p-7 md:p-8 transition-colors group" data-testid="booking-contact-email">
          <Mail className="w-5 h-5 text-[#D4A373]" strokeWidth={1.5} />
          <h3 className="mt-5 font-serif-x text-xl">{pick({ en: "Email", fr: "E-mail", es: "Correo" }, lang)}</h3>
          <p className="mt-2 text-sm text-[#FDFBF7]/85 group-hover:text-[#D4A373] transition-colors">xaluca@xaluca.com</p>
        </a>
        <a href="tel:+212535578450" className="bg-[#14100E] hover:bg-[#1A1513] p-7 md:p-8 transition-colors group" data-testid="booking-contact-phone">
          <Phone className="w-5 h-5 text-[#D4A373]" strokeWidth={1.5} />
          <h3 className="mt-5 font-serif-x text-xl">{pick({ en: "Phone", fr: "Téléphone", es: "Teléfono" }, lang)}</h3>
          <p className="mt-2 text-sm text-[#FDFBF7]/85 group-hover:text-[#D4A373] transition-colors">+212 535 57 84 50</p>
        </a>
        <a href="https://wa.me/2126000000000" target="_blank" rel="noreferrer noopener" className="bg-[#14100E] hover:bg-[#1A1513] p-7 md:p-8 transition-colors group" data-testid="booking-contact-whatsapp">
          <MessageCircle className="w-5 h-5 text-[#D4A373]" strokeWidth={1.5} />
          <h3 className="mt-5 font-serif-x text-xl">WhatsApp</h3>
          <p className="mt-2 text-sm text-[#FDFBF7]/85 group-hover:text-[#D4A373] transition-colors">+212 6XX XX XX XX</p>
          <p className="mt-1 text-[10px] tracking-[0.25em] uppercase text-[#FDFBF7]/40">
            {pick({ en: "Replaces upon confirmation", fr: "À confirmer", es: "Por confirmar" }, lang)}
          </p>
        </a>
        <a href="https://www.instagram.com/grupxaluca/" target="_blank" rel="noreferrer noopener" className="bg-[#14100E] hover:bg-[#1A1513] p-7 md:p-8 transition-colors group" data-testid="booking-contact-instagram">
          <Instagram className="w-5 h-5 text-[#D4A373]" strokeWidth={1.5} />
          <h3 className="mt-5 font-serif-x text-xl">Instagram</h3>
          <p className="mt-2 text-sm text-[#FDFBF7]/85 group-hover:text-[#D4A373] transition-colors">@grupxaluca</p>
        </a>
      </div>
    </div>
  </section>
);


// ───────────────────────────────────────────────────────────────────────────
// Mobile wizard navigation strip
const MobileWizard = ({ step, setStep, total, lang }) => (
  <div className="md:hidden sticky top-[64px] z-20 bg-[#FDFBF7]/95 backdrop-blur-md border-b border-[#2C2621]/10" data-testid="booking-mobile-wizard">
    <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
      <button
        type="button"
        onClick={() => setStep(Math.max(0, step - 1))}
        disabled={step === 0}
        className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#2C2621] disabled:opacity-30"
        data-testid="booking-mobile-prev"
      >
        <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
        {pick({ en: "Back", fr: "Retour", es: "Atrás" }, lang)}
      </button>
      <span className="text-[10px] tracking-[0.3em] uppercase text-[#A07042] tabular-nums">
        {String(step + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
      </span>
      <button
        type="button"
        onClick={() => setStep(Math.min(total - 1, step + 1))}
        disabled={step === total - 1}
        className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#2C2621] disabled:opacity-30"
        data-testid="booking-mobile-next"
      >
        {pick({ en: "Next", fr: "Suivant", es: "Siguiente" }, lang)}
        <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
      </button>
    </div>
    <div className="h-[2px] bg-[#2C2621]/10">
      <div
        className="h-full bg-[#C16542] transition-all duration-500"
        style={{ width: `${((step + 1) / total) * 100}%` }}
      />
    </div>
  </div>
);

// ───────────────────────────────────────────────────────────────────────────
// Main page
// `embedded` mode hides the page-level hero, cross-link banner and bottom
// info sections (BookingInformation / OTAPlatforms / GetInTouch). Used by
// the consolidated /contact hub which provides its own framing.
const BookingPage = ({ embedded = false } = {}) => {
  const { lang } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const formRef = useRef(null);
  // Contact-details guided-validation refs. When the user clicks
  // "Send Inquiry" and required contact fields are missing we (1)
  // smooth-scroll the page to this section, (2) focus the first
  // invalid input and (3) render inline red error messages under each
  // missing field. State is cleared automatically as the user types.
  const detailsRef = useRef(null);
  const nameInputRef = useRef(null);
  const emailInputRef = useRef(null);
  const phoneInputRef = useRef(null);

  // Pre-select via URL params. Supports three formats (most specific
  // wins):
  //   1. items={URI-encoded JSON} — rich per-property payload from the
  //      homepage HomeBookingWidget. Each item carries its own
  //      check_in, check_out, adults, children, infants, rooms and
  //      room_type. Allows the same property to appear multiple times.
  //   2. hotels=slug1,slug2&check_in=…&check_out=…&adults=…&… — shared
  //      dates/guests applied to every selected property.
  //   3. hotel=slug&service=spa — legacy single-property shorthand.
  const initial = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const itemsParam = params.get("items");
    const hotelParam = params.get("hotel");
    const hotelsParam = params.get("hotels");
    const serviceParam = params.get("service");

    // (1) Rich JSON payload — preferred. URLSearchParams.get() already
    //     URL-decodes the value, so we only need to JSON.parse it.
    //     Fall back to a second decodeURIComponent for legacy links
    //     that double-encoded the payload.
    let richItems = null;
    if (itemsParam) {
      const tryParse = (raw) => {
        try { return JSON.parse(raw); } catch { return null; }
      };
      let parsed = tryParse(itemsParam);
      if (!parsed) {
        try { parsed = tryParse(decodeURIComponent(itemsParam)); } catch (err) { devLog.debug("[BookingPage] items param decode failed", err); /* noop */ }
      }
      if (Array.isArray(parsed) && parsed.length > 0) richItems = parsed;
    }

    // (2) Shared-form fallback
    const shared = {
      check_in: params.get("check_in") || "",
      check_out: params.get("check_out") || "",
      adults: Number(params.get("adults")) || 2,
      children: Number(params.get("children")) || 0,
      infants: Number(params.get("infants")) || 0,
      rooms: Number(params.get("rooms")) || 1,
      room_type: params.get("room_type") || "",
    };
    const hotels = hotelsParam
      ? hotelsParam.split(",").map((s) => s.trim()).filter(Boolean)
      : (hotelParam ? [hotelParam] : []);

    return {
      richItems,
      hotels,
      services: serviceParam ? [serviceParam] : [],
      shared,
    };
  }, [location.search]);

  // Build the per-property detail map. Rich items win; fall back to
  // shared-form defaults for the older URL formats. Each property
  // detail now carries an array of independently-configured rooms.
  const buildInitialSelectedHotels = () => {
    if (initial.richItems) {
      // Merge duplicates of the same slug — the /booking UI keys by
      // slug, so two rows of "kasbah-arfoud" combine into one row that
      // concatenates their rooms arrays, keeps the earliest check_in
      // and latest check_out, and appends notes.
      const out = {};
      for (const it of initial.richItems) {
        if (!it || !it.slug) continue;
        // Normalise: support both new `rooms: [...]` shape and the
        // legacy flat `{adults, children, rooms (count), room_type,
        // board_basis}` shape from older URL links.
        const itemRooms = Array.isArray(it.rooms)
          ? it.rooms.map((r) => ({
              room_type: r.room_type || "",
              adults: Number(r.adults) || 2,
              children: Number(r.children) || 0,
              infants: Number(r.infants) || 0,
              board_basis: r.board_basis || "breakfast",
            }))
          : Array.from({ length: Math.max(1, Number(it.rooms) || 1) }, () => ({
              room_type: it.room_type || "",
              adults: Number(it.adults) || 2,
              children: Number(it.children) || 0,
              infants: Number(it.infants) || 0,
              board_basis: it.board_basis || "breakfast",
            }));
        const cur = out[it.slug];
        if (!cur) {
          out[it.slug] = {
            check_in: it.check_in || "",
            check_out: it.check_out || "",
            notes: it.notes || "",
            rooms: itemRooms,
          };
        } else {
          if (it.check_in && (!cur.check_in || it.check_in < cur.check_in)) cur.check_in = it.check_in;
          if (it.check_out && (!cur.check_out || it.check_out > cur.check_out)) cur.check_out = it.check_out;
          if (it.notes) cur.notes = cur.notes ? `${cur.notes}\n\n— ${it.notes}` : it.notes;
          cur.rooms = [...cur.rooms, ...itemRooms];
        }
      }
      return out;
    }
    return initial.hotels.reduce((acc, slug) => ({
      ...acc,
      [slug]: {
        check_in: initial.shared.check_in,
        check_out: initial.shared.check_out,
        notes: "",
        rooms: Array.from({ length: Math.max(1, initial.shared.rooms || 1) }, () => ({
          room_type: initial.shared.room_type || "",
          adults: initial.shared.adults,
          children: initial.shared.children,
          infants: initial.shared.infants || 0,
          board_basis: "breakfast",
        })),
      },
    }), {});
  };

  // ── Wire to the shared BookingContext ─────────────────────────────
  // The page no longer owns its own state. All booking state lives in
  // the BookingProvider (which also persists to localStorage), so the
  // homepage widget, the /booking workflow, the card grid, the
  // floating summary, and the final submission all read/write the
  // same source of truth.
  const {
    selectedHotels,
    selectedServices,
    selectedHotelSlugs,
    selectedServiceKeys,
    form,
    setForm,
    fieldErrors,
    fieldErrorsSet: setFieldErrors,
    clearFieldError,
    isSubmitting,
    toggleHotelWithFeedback: handleCardToggle,
    updateHotelDetail,
    toggleService,
    updateServiceDetail,
    setSelectedHotels,
    setSelectedServices,
    submit: contextSubmit,
  } = useBooking();

  const [step, setStep] = useState(0); // mobile wizard step only — UI-only, not persisted

  // Hero secondary CTA opens the "Tu viaje empieza con una
  // conversación" informative modal instead of scrolling to a
  // legacy #get-in-touch anchor. Kept as page-level state so the
  // modal's "Open the planner" CTA can also close itself + trigger
  // the same scroll-to-step-01 helper used everywhere else.
  const [infoModalOpen, setInfoModalOpen] = useState(false);

  const scrollToPlanner = () => {
    if (typeof window === "undefined") return;
    const el = document.querySelector("[data-testid='booking-step-hotels']");
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 120;
      window.scrollTo({ top: Math.max(y, 0), behavior: "smooth" });
    }
  };

  // Seed the shared booking state from URL params on first mount
  // (e.g. when a user lands here from a hotel-detail "Book this stay"
  // CTA). If there is no relevant URL param the existing localStorage-
  // hydrated state is preserved.
  // We intentionally only run this once on mount — re-running on every
  // location change would clobber the user's in-progress edits.
  useEffect(() => {
    const hasUrlIntent =
      (initial.richItems && initial.richItems.length > 0) ||
      initial.hotels.length > 0 ||
      initial.services.length > 0;
    if (!hasUrlIntent) return;
    const nextHotels = buildInitialSelectedHotels();
    const nextServices = initial.services.reduce(
      (acc, key) => ({
        ...acc,
        [key]: { date: "", time: "", people: 2, duration: "", pickup: "", requirements: "", notes: "" },
      }),
      {}
    );
    // URL intent wins — replace any previous in-progress state with
    // what the deep link asks for. This matches user expectation when
    // they click a fresh "Book this hotel" CTA from a property page.
    setSelectedHotels(nextHotels);
    setSelectedServices(nextServices);
  }, []);

  // Reset the mobile-wizard step back to 0 (Accommodation) whenever
  // the shared booking context becomes fully empty — for example after
  // the user confirms a "Clear all" reset from the widget. Keeps the
  // /booking page in lock-step with the rest of the unified booking
  // experience.
  useEffect(() => {
    if (selectedHotelSlugs.length === 0 && selectedServiceKeys.length === 0) {
      setStep(0);
    }
  }, [selectedHotelSlugs.length, selectedServiceKeys.length]);

  // Submit wrapper for the BookingPage forms — calls into the context's
  // shared submit() helper, then drives the page-specific guided-
  // validation focus + scroll UX (focus first invalid input, scroll
  // the details section into view).
  const submit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const result = await contextSubmit({ source: "booking-page", serviceCatalogue: SERVICE_CATALOGUE });
    if (result?.reason === "validation") {
      // Pick the first invalid input ref in DOM order so focus follows
      // the natural reading flow.
      const errs = result.errors || {};
      const firstInvalidRef =
        (errs.full_name && nameInputRef) ||
        (errs.email && emailInputRef) ||
        (errs.phone && phoneInputRef) ||
        null;
      const scrollTarget = firstInvalidRef?.current || detailsRef.current;
      if (scrollTarget && typeof scrollTarget.scrollIntoView === "function") {
        scrollTarget.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      window.setTimeout(() => {
        firstInvalidRef?.current?.focus?.();
      }, 350);
      toast(pick(COPY.validation_summary, lang));
    } else if (result?.ok) {
      // Successful submission — reset the local mobile-wizard step so
      // the user can start a new enquiry from step 1 immediately.
      setStep(0);
    }
  };

  // Smooth-scrolls to the form / details section. Used by the hero
  // CTA on this page.
  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };



  // Desktop: render all three steps stacked. Mobile: wizard.
  const stepRenderers = [
    <HotelsStep key="s1" hotels={HOTELS} selectedHotels={selectedHotels} toggleHotel={handleCardToggle} updateHotelDetail={updateHotelDetail} lang={lang} />,
    <ServicesStep key="s2" selectedServices={selectedServices} toggleService={toggleService} updateServiceDetail={updateServiceDetail} lang={lang} />,
    <DetailsStep
      key="s3"
      form={form}
      setForm={setForm}
      lang={lang}
      errors={fieldErrors}
      clearError={clearFieldError}
      nameInputRef={nameInputRef}
      emailInputRef={emailInputRef}
      phoneInputRef={phoneInputRef}
    />,
  ];

  // ── Booking sync ───────────────────────────────────────────────────
  // The /booking workflow and the HomeBookingWidget (mounted on every
  // other page) read from / write to the SAME `BookingContext`. There
  // is no longer an embedded copy of the widget on this page, so the
  // legacy `widgetBlocks` / `handleWidgetChange` helpers have been
  // retired. Users who start an inquiry in the widget land on the
  // workflow below with every selection already populated.

  const continueToDetails = () => {
    // Scroll to the existing hotels step / form section. Works for both
    // desktop (stacked) and mobile (wizard) layouts.
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div data-testid="page-booking" className="bg-[#FDFBF7] text-[#2C2621]">
      {!embedded && (
        <Hero
          lang={lang}
          onSecondaryClick={() => setInfoModalOpen(true)}
          onOpenQuick={() => navigate("/classic-booking")}
          onOpenInquiry={() => navigate("/contact?tab=inquiry")}
        />
      )}

      {/* Cross-link: prefer the faster, classic flow */}
      {!embedded && (
        <div className="bg-[#F2EBE1]/40 border-b border-[#2C2621]/10" data-testid="booking-switch-banner">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <p className="text-sm text-[#5C5248] inline-flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#A07042]" strokeWidth={1.5} />
              {pick({
                en: "Looking for a faster request form?",
                fr: "Vous préférez un formulaire plus rapide ?",
                es: "¿Prefiere un formulario más rápido?",
              }, lang)}
            </p>
            <Link
              to="/classic-booking"
              className="group inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-[#C16542] hover:text-[#A35133] border-b border-[#C16542]/40 hover:border-[#A35133] pb-1 transition-colors"
              data-testid="booking-switch-to-classic"
            >
              {pick({ en: "Switch to classic booking", fr: "Passer à la demande express", es: "Cambiar a solicitud clásica" }, lang)}
              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      )}

      {/* The Booking Workflow below IS the canonical interface on
          this page — the floating HomeBookingWidget is intentionally
          NOT mounted here to avoid showing two booking systems on the
          same screen. Both surfaces share `BookingContext`, so any
          inquiry started in the widget elsewhere on the site flows
          straight into the workflow below without re-entry. */}

      {/* "How to book with us" — editorial step-by-step strip applying to
          every Grup Xaluca address (hotels, riads, refuge, desert camps). */}
      {!embedded ? <HowToBookSection /> : null}

      {isMobile && <MobileWizard step={step} setStep={setStep} total={3} lang={lang} />}

      <div ref={formRef} className="relative">
        {isMobile ? (
          <section className="py-16">
            <div className="max-w-7xl mx-auto px-6">{stepRenderers[step]}</div>
            <div className="max-w-7xl mx-auto px-6 mt-10 flex flex-col sm:flex-row gap-3">
              {step < 2 ? (
                <button
                  type="button"
                  onClick={() => setStep(step + 1)}
                  className="w-full inline-flex items-center justify-center gap-3 bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] px-7 py-4 text-[11px] tracking-[0.3em] uppercase transition-colors"
                  data-testid="booking-mobile-continue"
                >
                  {pick({ en: "Continue", fr: "Continuer", es: "Continuar" }, lang)}
                  <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={submit}
                  disabled={isSubmitting}
                  className="w-full inline-flex items-center justify-center gap-3 bg-[#C16542] hover:bg-[#A35133] disabled:opacity-60 text-[#FDFBF7] px-7 py-4 text-[11px] tracking-[0.3em] uppercase transition-colors"
                  data-testid="booking-mobile-submit"
                >
                  {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-3.5 h-3.5" strokeWidth={1.5} />}
                  {isSubmitting ? pick(COPY.sending, lang) : pick(COPY.submit, lang)}
                </button>
              )}
            </div>
          </section>
        ) : (
          <>
            <section className="py-20 md:py-28 bg-[#FDFBF7]">
              <div className="max-w-7xl mx-auto px-6 md:px-12">{stepRenderers[0]}</div>
            </section>
            <section className="py-20 md:py-28 bg-[#F2EBE1]/40 border-y border-[#2C2621]/10">
              <div className="max-w-7xl mx-auto px-6 md:px-12">{stepRenderers[1]}</div>
            </section>
            <section ref={detailsRef} className="py-20 md:py-28 bg-[#FDFBF7]" data-testid="booking-details-section">
              <div className="max-w-7xl mx-auto px-6 md:px-12">
                <form onSubmit={submit} noValidate>
                  {stepRenderers[2]}
                  <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pt-10 border-t border-[#2C2621]/15">
                    <p className="text-sm text-[#5C5248] max-w-md">
                      {pick({
                        en: "No deposit required. We answer within 24 hours, in your language.",
                        fr: "Aucun acompte requis. Nous répondons sous 24 heures, dans votre langue.",
                        es: "Sin depósito. Respondemos en 24 horas, en tu idioma.",
                      }, lang)}
                    </p>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="group inline-flex items-center justify-center gap-3 bg-[#C16542] hover:bg-[#A35133] disabled:opacity-60 text-[#FDFBF7] px-8 py-4 text-[11px] tracking-[0.3em] uppercase transition-colors whitespace-nowrap"
                      data-testid="booking-submit"
                    >
                      {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-3.5 h-3.5" strokeWidth={1.5} />}
                      {isSubmitting ? pick(COPY.sending, lang) : pick(COPY.submit, lang)}
                      {!isSubmitting && <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" strokeWidth={1.5} />}
                    </button>
                  </div>
                </form>
              </div>
            </section>
          </>
        )}
      </div>

      {!embedded && <BookingInformation lang={lang} />}
      {!embedded && <OTAPlatforms lang={lang} />}
      {!embedded && <GetInTouch lang={lang} />}

      {/* The floating enquiry summary is now mounted globally in App.js
          via the StickySummary component, so it stays in sync across
          every page where the user has an active enquiry. */}
      {/* Hero-triggered informative modal — explains the five request
          families a booking enquiry can carry. Portalled to <body>. */}
      <BookingInfoModal
        open={infoModalOpen}
        lang={lang}
        onClose={() => setInfoModalOpen(false)}
        onOpenPlanner={scrollToPlanner}
      />
    </div>
  );
};

export default BookingPage;
